'use client';

import type { ModalProps } from '@florencelabs/modal-2';
import { Button } from '@florencelabs/button';
import { Modal, Dialog } from '@florencelabs/modal-2';
import clsx from 'clsx';
import { ReactElement } from 'react';
import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { useSearchFilters } from '@provider-directory/contexts/SearchFiltersContext';

interface ProviderSearchFiltersDrawerProps extends ModalProps {
  isLoading: boolean;
  totalCount: number;
  onClear?: () => void;
}

export const ProviderSearchFiltersDrawer = ({
  children,
  isLoading,
  totalCount,
  onClear,
}: ProviderSearchFiltersDrawerProps): ReactElement => {
  const [t] = useTranslation();
  const { isOpen, setIsOpen, notifyShowResults, resultsCountOverride } =
    useSearchFilters();
  const countToDisplay = resultsCountOverride ?? totalCount;
  return (
    <>
      <Button
        testId={'mobile-search-filters-drawer-btn'}
        onPress={() => {
          setIsOpen(true);
        }}
        variant={'clientMain'}
        icon={'tune'}
        className={clsx(
          'flex items-center justify-center gap-x-4',
          'h-[3.75rem] !rounded-none',
          'fixed bottom-0 left-0 right-0',
          'focus-visible:outline-0',
          'z-[5]',
        )}
      >
        {t('searchFilters.filterYourSearch')}
      </Button>
      <Modal
        isOpen={isOpen}
        variant={'bottomDrawer'}
        onOpenChange={setIsOpen}
        className={'h-[100dvh] w-full'}
      >
        <Dialog
          aria-label={t('searchFilters.filters')}
          className={'relative h-full w-full outline-none'}
        >
          <>
            <Heading
              slot={'title'}
              className={
                'flex w-full items-center justify-center border-b border-gray-200 py-6 text-body-medium-medium'
              }
            >
              {t('searchFilters.filters')}
            </Heading>
            <Button
              slot={null}
              variant={'systemLink'}
              icon={'close'}
              iconPosition={'only'}
              className={'absolute right-5 top-5'}
              testId={'drawerCloseButton'}
              aria-label={t('searchFilters.closeFilters')}
              onPress={() => {
                setIsOpen(false);
              }}
            />
            {children}
            <div
              className={clsx(
                'absolute bottom-0 left-0 right-0 h-[5.125rem]',
                'flex items-center',
              )}
            >
              <Button
                testId={'drawer-search-filters-clear-btn'}
                slot={null}
                className={'h-full w-full !rounded-none'}
                variant={'clientMain'}
                onPress={() => {
                  onClear?.();
                  setIsOpen(false);
                }}
              >
                {t('searchFilters.clear')}
              </Button>
              <div
                className={'flex h-full w-[1px] items-center bg-primary-color'}
              >
                <div className={'h-8 w-[1px] shrink-0 bg-white opacity-20'} />
              </div>
              <Button
                testId={'drawer-search-filters-showResults-btn'}
                slot={null}
                onPress={() => {
                  notifyShowResults?.();
                  setIsOpen(false);
                }}
                className={'h-full w-full !rounded-none'}
                variant={'clientMain'}
                isLoading={isLoading}
              >
                {t('searchFilters.showResultsCount', {
                  count: countToDisplay,
                })}
              </Button>
            </div>
          </>
        </Dialog>
      </Modal>
    </>
  );
};
